"use client";
import { useRef, useEffect, useState } from "react";
import { useTheme } from "next-themes";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

function MapboxMap() {
  const [map, setMap] = useState(null);
  const mapNode = useRef(null);
  const { resolvedTheme } = useTheme();
  const [error, setError] = useState(false);
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const rotationRef = useRef(170); // Shifted 30 degrees to the right from 140
  const rotationFrameRef = useRef(null);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const initialZoom = isMobile ? 11 : 9;
  const rotationZoom = isMobile ? 10 : 11;
  const introZoom = isMobile ? 10 : 11;
  const initialPitch = isMobile ? 55 : 0;
  const center = [-79.6371, 43.7416]; // Original center

  // Custom colors for mask gradients
  const customLightBlue = "#efefef";
  const customGrey = "#071222";

  const addLandmarkBuildings = (mapInstance) => {
    if (!mapInstance.getLayer("landmarks")) {
      mapInstance.addLayer({
        id: "landmarks",
        source: "composite",
        "source-layer": "building",
        filter: ["all", ["==", "extrude", "true"], [">", "height", 150]],
        type: "fill-extrusion",
        minzoom: 10,
        paint: {
          "fill-extrusion-color": "#2563eb",
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.9,
        },
      });
    }
  };

  const add3DBuildings = (mapInstance) => {
    if (!mapInstance.getLayer("3d-buildings")) {
      mapInstance.addLayer({
        id: "3d-buildings",
        source: "composite",
        "source-layer": "building",
        filter: ["==", "extrude", "true"],
        type: "fill-extrusion",
        minzoom: 13, // Delay loading for performance
        paint: {
          "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            ["get", "height"],
            0, "#4a6fa5",
            50, "#3b82f6",
            100, "#2563eb",
            150, "#1d4ed8",
          ],
          "fill-extrusion-height": ["get", "height"],
          "fill-extrusion-base": ["get", "min_height"],
          "fill-extrusion-opacity": 0.6, // Lighter for performance
        },
      });
    }
  };

  useEffect(() => {
    const node = mapNode.current;
    if (typeof window === "undefined" || node === null) return;

    mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
    if (!mapboxgl.accessToken) {
      setError(true);
      return;
    }

    // Use satellite-streets for dark mode
    const mapStyle = resolvedTheme === "dark" 
      ? "mapbox://styles/mapbox/satellite-streets-v12" 
      : "mapbox://styles/mapbox/light-v11";

    const mapboxMap = new mapboxgl.Map({
      container: node,
      style: mapStyle,
      center: center,
      zoom: initialZoom,
      pitch: initialPitch,
      bearing: 170, // Shifted 30 degrees to the right from 140
      interactive: false,
      dragPan: false,
      scrollZoom: false,
    });

    mapboxMap.on("style.load", () => {
      setStyleLoaded(true);

      const layersToRemove = [
        "poi",
        "poi-label",
        "transit",
        "transit-label",
        "road-label",
        "water-point-label",
        "water-line-label",
        "natural-point-label",
        "natural-line-label",
        "waterway-label",
      ];
      layersToRemove.forEach((layer) => {
        if (mapboxMap.getLayer(layer)) mapboxMap.removeLayer(layer);
      });

      ["place-city", "place-town"].forEach((layerId) => {
        if (mapboxMap.getLayer(layerId)) {
          mapboxMap.setPaintProperty(layerId, "text-color", "#2563eb");
        }
      });

      addLandmarkBuildings(mapboxMap);
      if (isMobile) add3DBuildings(mapboxMap);

      setTimeout(() => {
        if (isMobile) {
          mapboxMap.flyTo({
            zoom: introZoom,
            pitch: 55,
            bearing: 180, // Shifted 30 degrees to the right from 150
            duration: 4000, // Shortened for performance
            essential: true,
            curve: 1.5,
            speed: 0.8,
          });
          mapboxMap.once("moveend", () => {
            mapboxMap.flyTo({
              pitch: 45,
              zoom: rotationZoom,
              bearing: 170, // Shifted 30 degrees to the right
              duration: 2000,
              essential: true,
              curve: 1.5,
            });
            setIsZoomedIn(false);
          });
        } else {
          mapboxMap.flyTo({
            zoom: introZoom,
            pitch: 45,
            bearing: 180, // Shifted 30 degrees to the right
            duration: 4000,
            essential: true,
            curve: 1.5,
            speed: 0.8,
          });
          mapboxMap.once("moveend", () => {
            mapboxMap.flyTo({
              pitch: 60,
              zoom: rotationZoom,
              bearing: 170, // Shifted 30 degrees to the right
              duration: 2000,
              essential: true,
              curve: 1.5,
            });
          });
        }
      }, 100);
    });

    setMap(mapboxMap);
    return () => mapboxMap.remove();
  }, [resolvedTheme, initialZoom, rotationZoom, introZoom, initialPitch, isMobile]);

  useEffect(() => {
    if (!map || !styleLoaded) return;

    let lastTimestamp = null;
    const rotateMap = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const deltaTime = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;
      const rotationSpeed = 1; // Slower for performance
      rotationRef.current = (rotationRef.current + rotationSpeed * deltaTime) % 360;
      map.setBearing(rotationRef.current);
      rotationFrameRef.current = requestAnimationFrame(rotateMap);
    };

    const startRotation = () => {
      if (!isZoomedIn && !rotationFrameRef.current) {
        rotationFrameRef.current = requestAnimationFrame(rotateMap);
      }
    };

    const stopRotation = () => {
      if (rotationFrameRef.current) {
        cancelAnimationFrame(rotationFrameRef.current);
        rotationFrameRef.current = null;
      }
    };

    const timeout = setTimeout(startRotation, 6100); // Adjusted for shorter intro
    return () => {
      clearTimeout(timeout);
      stopRotation();
    };
  }, [map, styleLoaded, isZoomedIn]);

  const handleZoomIn = (event) => {
    if (!map || !styleLoaded) return;
    map.stop();
    if (rotationFrameRef.current) {
      cancelAnimationFrame(rotationFrameRef.current);
      rotationFrameRef.current = null;
    }

    const { clientX, clientY } = event;
    const coordinates = map.unproject([clientX, clientY]);

    if (isZoomedIn) {
      if (map.getLayer("3d-buildings")) map.removeLayer("3d-buildings");
      map.flyTo({
        center: center,
        zoom: rotationZoom,
        pitch: 45,
        bearing: rotationRef.current,
        duration: 2500,
        essential: true,
        curve: 1.5,
      });
      setIsZoomedIn(false);
      map.once("moveend", startRotation);
    } else {
      map.flyTo({
        center: coordinates,
        zoom: 16,
        pitch: 65,
        bearing: rotationRef.current,
        duration: 3000,
        essential: true,
        curve: 1.5,
      });
      setIsZoomedIn(true);
      add3DBuildings(map);
    }
  };

  if (error) return <div className="w-full h-full bg-gray-100 dark:bg-gray-800" />;

  // Determine gradient based on theme
  const maskGradient = resolvedTheme === "dark"
    ? `linear-gradient(to bottom, #071222 0%, #071222 45%, transparent 100%)`
    : `linear-gradient(to bottom, #efefef 0%, #efefef 45%, transparent 100%)`;

  return (
    <span className="cursor-pointer" onClick={handleZoomIn}>
      <div
        ref={mapNode}
        style={{
          width: "100vw",
          height: "100vh",
          opacity: .7,
          WebkitMaskImage: maskGradient,
          maskImage: maskGradient,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
        }}
      />
      
    </span>
  );
}

export default MapboxMap;
